import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
  ResultError,
  ResultInfo,
  ResultSubtract,
  ResultSuccess,
  CloseIcon,
} from "@/ImageRepo";
import ShepButton from "./ShepButton";
import "./ShepAlert.css";
import {
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Paper,
  Snackbar,
  TextField
} from "@mui/material";

import { selectAllAlerts } from "@/features/homepage/alertSliceList";
import { styled } from "@mui/material/styles";

const alertTimeout = 12000; // ms

// Default initial state for Alert slice if not provided
const Alert = ({
  status = null,
  alertType = "single",
  title = null,
  message = null,
  link = null,
  linkLabel = "Link",
  buttonLabel = null,
  onButtonClick,
  buttonLabel2 = "Close",
  onButtonClick2,
  intValue = 0,
  setIntValue,
  onClose,
}) => {
  const alerts = useSelector(selectAllAlerts);

  const [alert, setAlert] = useState({
    status: status,
    alertType: alertType,
    title: title,
    message: message,
    link: link,
    linkLabel: linkLabel,
    buttonlabel: buttonLabel,
    onButtonClick: onButtonClick,
    buttonlabel2: buttonLabel2,
    onButtonClick2: onButtonClick2,

    intValue: intValue,
    setIntValue: setIntValue,
    onClose: onClose,
  });

  // For each element in the array list, display it for the specified duration and hide after the timeout
  useEffect(() => {
    if (alerts.length > 0) {
      setAlert(alerts[alerts.length - 1]);
      onAlertOpen();
    }
  }, [alerts]);

  // Hide the alert when close button clicked
  const onAlertClose = () => {
    setIsAlertVisible(false);
    // console.debug("Close btn clicked, hiding alert", isAlertVisible);

    // call user defined close function if defined
    if (alert.onClose) {
      alert.onClose();
    }
  };

  const onAlertOpen = () => {
    setIsAlertVisible(true);
    setSingleAlertOpen();
  };

  const onButtonClose = () => {
    if (alert.onButtonClick) {
      alert.onButtonClick();
    }
    onAlertClose();
  };

  const onButton2Close = () => {
    if (alert.onButtonClick2) {
      alert.onButtonClick2();
    }
    onAlertClose();
  };

  // states to control single alert visibility so that auto-hide does not affect other components
  const [openSingle, setOpenSingle] = useState(false);

  // // State variable to control whether to show or hide the alert
  const [isAlertVisible, setIsAlertVisible] = useState(false);

  const setSingleAlertOpen = () => {
    setOpenSingle(true);
  };

  const handleSingleAlertClose = (event, reason) => {
    // Only close alert if user clicks close icon
    if (reason === "clickaway") {
      return;
    }
    setOpenSingle(false);
  };

  // Returns and selects the appropriate alert icon based on type
  const renderIcon = (className = "status-icon") => {
    switch (alert.status) {
      case "Success":
        return <ResultSuccess className={className} />;
      case "Warning":
        return <ResultSubtract className={className} />;
      case "Error":
        return <ResultError className={className} />;
      case "Info":
        return <ResultInfo className={className} />;
      default:
        return null;
    }
  };

  // Returns link component with styling based on whether alert is single line or multi-line type
  const renderLink = () => {
    return (
      alert.link && (
        <a
          href={alert.link}
          className={
            alert.alertType === "single" ? "alert-link" : "multi-alert-link"
          }
        >
          {linkLabel}
        </a>
      )
    );
  };

  // Returns reusable button
  const renderButton = (
    btnLabel = alert.buttonLabel,
    onClick = onButtonClose, //alert.onButtonClick ? alert.onButtonClick : onAlertClose,
    size = "small",
    variant = "primary",
    className = ""
  ) => {
    return (
      btnLabel && (
        <ShepButton
          size={size}
          variant={variant}
          onClick={onClick}
          className={className}
        >
          {btnLabel}
        </ShepButton>
      )
    );
  };

  // Returns a single line banner style alert component with fade in transition which automatically hids after specified duration
  const renderSingleLine = () => {
    return (
      <>
        <div
          className={`shep-alert "single-line" ${alert.status.toLowerCase()}`}
        >
          {renderIcon()}

          <div className="content">
            <p className="message">{alert.message}</p>
          </div>

          {renderLink()}
          {renderButton(alert.buttonLabel)}

          <div>
            <CloseIcon className="close-icon" onClick={onAlertClose} />
          </div>
        </div>
      </>
    );
  };

  // Sets dialog alert styling based on status type
  const setDialogPaper = () => {
    let bgColor = "var(--disabled)";
    let borderColor = "var(--text-primary)";

    switch (alert.status) {
      case "Success":
        bgColor = "var(--status-successful-pale)";
        borderColor = "var(--status-successful-intense)";
        break;
      case "Warning":
        bgColor = "var(--status-processing-pale)";
        borderColor = "var(--status-processing)";
        break;
      case "Error":
        bgColor = "var(--status-error-pale)";
        borderColor = "var(--status-error-intense)";
        break;
      case "Info":
        bgColor = "var(--tertiary-13)";
        borderColor = "var(--status-upcoming-intense)";
        break;
      default:
        break;
    }

    const StyledDialogPaper = styled(Paper)({
      backgroundColor: bgColor,
      border: "1px solid",
      borderColor: borderColor,
    });

    return StyledDialogPaper;
  };
  //{/*open={alert.isAlertVisible}*/}
  // Returns a Dialog style Alert which is multiple lines (title, message) requiring user acknowledgement
  const renderMultiLine = () => {
    return (
      <>
        <Dialog
          open={isAlertVisible}
          onClose={onAlertClose}
          className={`"multi-line"`}
          maxWidth="sm"
          fullWidth
          PaperComponent={setDialogPaper()}
          sx={{ zIndex: 1900 }}
        >
          <div
            className="icon-container"
            style={{ marginLeft: "20px", marginTop: "37px" }}
          >
            {renderIcon()}
          </div>
          <div className="dialog">
            <DialogTitle
              className="title"
              id="alert-dialog-title"
              style={{
                padding: "0px",
                marginBottom: "12px",
                marginTop: "20px",
              }}
            >
              {alert.title}
            </DialogTitle>
            <DialogContent
              className="dialog-content"
              style={{ padding: "0px" }}
            >
              <p className="message" style={{ margin: "0px" }}>
                {alert.message}
              </p>
            </DialogContent>
          </div>
          <div className="link-btn-container" style={{ marginBottom: "30px" }}>
            {renderLink()}
            {renderButton(alert.buttonLabel)}
          </div>

          <div className="close-container">
            <CloseIcon
              className="close-icon"
              onClick={onAlertClose}
              style={{
                marginRight: "16px",
                marginTop: "16px",
                marginBottom: "12px",
                marginLeft: "4px",
              }}
            />
          </div>
        </Dialog>
      </>
    );
  };

  const [currentValue, setCurrentValue] = useState(alert.value)

  const renderIntInput = () => {
    const setParentValue = alert.setIntValue
    return (
      <>
        <TextField
          variant="outlined"
          name="integerInput"
          value={currentValue}
          onChange={(event) => {
            if (!isNaN(event.target.value)) {
              setCurrentValue(event.target.value);
              setParentValue(event.target.value);
            }
          }}
          sx={{ width: 180, marginTop: 1, marginBottom: 2 }}
        />
      </>
    )
  };

  const resultsButton = () => {
    return (
      <>
        <div className="results-multi-button">
          {alert.buttonLabel2 &&
            renderButton(
              alert.buttonLabel2,
              onButton2Close,
              // alert.onButtonClick2 ? alert.onButtonClick2 : onAlertClose,
              "small",
              "secondary",
              "second-button"
            )}

          {renderButton()}
        </div>
      </>
    );
  };
  //open = { alert.isAlertVisible }
  // Returns a Dialog style for the results component
  const renderResults = () => {
    return (
      <>
        <Dialog
          open={isAlertVisible}
          onClose={onAlertClose}
          className={`results`}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              border: "0px",
            },
          }}
          sx={{ zIndex: 1900 }}
        >
          <div className="results-icon-container">
            {renderIcon("results-status-icon")}
          </div>
          <div className="dialog" style={{ marginBottom: "0px" }}>
            <DialogTitle
              className="title"
              id="alert-dialog-title"
              style={{ padding: "0px", marginBottom: "12px" }}
            >
              {alert.title}
            </DialogTitle>
            <DialogContent
              className="dialog-content"
              style={{ padding: "0px", marginBottom: "32px" }}
            >
              <p
                className="results-message"
                style={{ margin: "0px", whiteSpace: "pre-line" }}
              >
                {alert.message}
              </p>
              {alert.setIntValue && (
                <p>
                  {renderIntInput()}
                </p>
              )}
            </DialogContent>
          </div>
          <DialogActions>
            <div
              className="link-btn-container"
              style={{ marginBottom: "28px" }}
            >
              {resultsButton()}
            </div>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  // Selects the appropriate alert content based on the alert type
  const selectAlertType = () => {
    switch (alert.alertType) {
      case "multi":
        return renderMultiLine();
      case "result":
        return renderResults();
      default:
        return (
          <Snackbar
            sx={{ marginTop: "54px", zIndex: 1900 }}
            open={openSingle}
            autoHideDuration={alertTimeout}
            onClose={handleSingleAlertClose}
            anchorOrigin={{ horizontal: "center", vertical: "top" }}
          >
            <div>{renderSingleLine()}</div>
          </Snackbar>
        );
    }
  };

  return (
    <>
      {isAlertVisible ? (
        <Container className={`alert 'shep-alert'`}>
          <div className="content">{selectAlertType()}</div>
        </Container>
      ) : null}
    </>
  );
};

Alert.propTypes = {
  status: PropTypes.oneOf(["Success", "Warning", "Error", "Info"]).isRequired,
  alertType: PropTypes.oneOf(["single", "multi", "result"]).isRequired,
  title: PropTypes.string,
  message: PropTypes.string.isRequired,
  link: PropTypes.string,
  linkLabel: PropTypes.string,
  buttonLabel: PropTypes.string,
  onButtonClick: PropTypes.func,
  onClose: PropTypes.func,
  showAlert: PropTypes.bool,
};

export default Alert;
